/**
 * Browser-side multi-turn chat for the Ask AI sidebar, using keys stored in
 * `berry.integrations.center.v1` (Integrations center). No Funner backend involved.
 *
 * CORS: Google Generative Language API allows browser calls with `?key=`. OpenAI often blocks
 * browser CORS — failures surface as network errors; the UI should show the message.
 */

export type LocalChatTurn = { role: 'user' | 'assistant'; content: string };

export type AskLocalLlmConversationParams = {
  integrationId: string;
  apiKey: string;
  model: string;
  /** Full conversation including the latest user message (no "loading" placeholders). */
  turns: LocalChatTurn[];
};

export type AskLocalLlmConversationResult =
  | { ok: true; text: string }
  | { ok: false; error: string; status?: number };

async function readHttpError(res: Response): Promise<string> {
  try {
    const j = (await res.json()) as { error?: { message?: string }; message?: string };
    return (j?.error?.message ?? j?.message ?? res.statusText) || `HTTP ${res.status}`;
  } catch {
    return res.statusText || `HTTP ${res.status}`;
  }
}

/** Concatenate text parts; embed images as markdown data-URIs for MarkdownRenderer. */
function parseGeminiResponseJson(data: unknown): string {
  const d = data as {
    candidates?: Array<{
      content?: {
        parts?: Array<{
          text?: string;
          inlineData?: { mimeType?: string; data?: string };
        }>;
      };
    }>;
  };
  const parts = d.candidates?.[0]?.content?.parts ?? [];
  let out = '';
  for (const p of parts) {
    if (p.text) out += p.text;
    if (p.inlineData?.data && p.inlineData.mimeType) {
      out += `\n\n![image](data:${p.inlineData.mimeType};base64,${p.inlineData.data})\n\n`;
    }
  }
  return out.trim();
}

async function askGeminiMultiTurn(
  apiKey: string,
  model: string,
  turns: LocalChatTurn[],
  systemInstruction?: string,
): Promise<AskLocalLlmConversationResult> {
  const modelId = model.replace(/^models\//, '').trim();
  const key = encodeURIComponent(apiKey.trim());
  const contents = turns.map((t) => ({
    role: t.role === 'assistant' ? 'model' : 'user',
    parts: [{ text: t.content }],
  }));

  const sys = systemInstruction?.trim();
  const body = JSON.stringify({
    ...(sys ? { systemInstruction: { parts: [{ text: sys }] } } : {}),
    contents,
    generationConfig: { maxOutputTokens: 8192, temperature: 0.4 },
  });

  const versions = ['v1beta', 'v1'] as const;
  let lastError = '';
  let lastStatus: number | undefined;

  for (const ver of versions) {
    const url = `https://generativelanguage.googleapis.com/${ver}/models/${encodeURIComponent(
      modelId,
    )}:generateContent?key=${key}`;

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });

    if (res.ok) {
      const data = await res.json();
      const text = parseGeminiResponseJson(data);
      if (!text) {
        return { ok: false, error: 'Empty response from Gemini' };
      }
      return { ok: true, text };
    }

    lastStatus = res.status;
    lastError = await readHttpError(res);
    const retry =
      res.status === 404 ||
      lastError.includes('is not found') ||
      (lastError.includes('not found') && lastError.includes('API version')) ||
      lastError.includes('not supported for generateContent');
    if (!retry || ver === 'v1') {
      return { ok: false, error: lastError, status: lastStatus };
    }
  }

  return { ok: false, error: lastError || 'Gemini request failed', status: lastStatus };
}

export type AskGeminiWithSystemInstructionParams = {
  apiKey: string;
  model: string;
  /** Hard system prompt (e.g. JSON-only template contract). */
  systemInstruction: string;
  /** End-user brief / question. */
  userMessage: string;
};

/**
 * Some models (e.g. Gemma) do not support the API `systemInstruction` / developer-instruction field
 * and return errors like "Developer instruction is not enabled for models/…".
 */
export function geminiModelSupportsDeveloperInstruction(modelId: string): boolean {
  const id = modelId.replace(/^models\//, '').trim().toLowerCase();
  if (!id) return true;
  if (id.includes('gemma')) return false;
  return true;
}

function mergeSystemAndUserForGemini(systemInstruction: string, userMessage: string): string {
  const sys = systemInstruction.trim();
  const usr = userMessage.trim();
  if (!sys) return usr;
  return `${sys}\n\n---\nUser request:\n${usr}`;
}

/**
 * Single-turn Gemini call with system prompt. Prefers `systemInstruction` in the JSON body when
 * the model supports it; otherwise merges system + user into one user message (Gemma and similar).
 * Retries once with merged prompt if the API rejects developer instructions.
 */
export async function askGeminiWithSystemInstruction(
  params: AskGeminiWithSystemInstructionParams,
): Promise<AskLocalLlmConversationResult> {
  const { apiKey, model, systemInstruction, userMessage } = params;
  const key = apiKey.trim();
  if (!key) {
    return { ok: false, error: 'Missing API key' };
  }
  const modelId = model.replace(/^models\//, '').trim();
  if (!modelId) {
    return { ok: false, error: 'Missing model' };
  }
  const sys = systemInstruction.trim();
  const usr = userMessage.trim();
  if (!usr) {
    return { ok: false, error: 'No user message' };
  }

  const generationConfig = { maxOutputTokens: 8192, temperature: 0.2 };

  const runGenerateContent = async (bodyStr: string): Promise<AskLocalLlmConversationResult> => {
    const versions = ['v1beta', 'v1'] as const;
    let lastError = '';
    let lastStatus: number | undefined;

    for (const ver of versions) {
      const url = `https://generativelanguage.googleapis.com/${ver}/models/${encodeURIComponent(
        modelId,
      )}:generateContent?key=${encodeURIComponent(key)}`;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: bodyStr,
      });

      if (res.ok) {
        const data = await res.json();
        const text = parseGeminiResponseJson(data);
        if (!text) {
          return { ok: false, error: 'Empty response from Gemini' };
        }
        return { ok: true, text };
      }

      lastStatus = res.status;
      lastError = await readHttpError(res);
      const retry =
        res.status === 404 ||
        lastError.includes('is not found') ||
        (lastError.includes('not found') && lastError.includes('API version')) ||
        lastError.includes('not supported for generateContent');
      if (!retry || ver === 'v1') {
        return { ok: false, error: lastError, status: lastStatus };
      }
    }

    return { ok: false, error: lastError || 'Gemini request failed', status: lastStatus };
  };

  const tryDeveloperInstruction = geminiModelSupportsDeveloperInstruction(modelId) && Boolean(sys);

  let first = await runGenerateContent(
    JSON.stringify(
      tryDeveloperInstruction
        ? {
            systemInstruction: { parts: [{ text: sys }] },
            contents: [{ role: 'user', parts: [{ text: usr }] }],
            generationConfig,
          }
        : {
            contents: [{ role: 'user', parts: [{ text: mergeSystemAndUserForGemini(sys, usr) }] }],
            generationConfig,
          },
    ),
  );

  if (first.ok) return first;

  const err = first.error || '';
  const shouldRetryMerged =
    tryDeveloperInstruction &&
    (err.includes('Developer instruction') ||
      err.includes('system instruction') ||
      err.includes('systemInstruction') ||
      err.includes('not enabled for'));

  if (shouldRetryMerged) {
    return runGenerateContent(
      JSON.stringify({
        contents: [{ role: 'user', parts: [{ text: mergeSystemAndUserForGemini(sys, usr) }] }],
        generationConfig,
      }),
    );
  }

  return first;
}

export type AskGeminiGenerateContentWithInlineMediaParams = {
  apiKey: string;
  model: string;
  /** User-visible instruction (e.g. analysis question or transcription instruction). */
  userTextPrompt: string;
  inlineMimeType: string;
  inlineBase64: string;
  systemInstruction?: string;
};

/**
 * Single user turn with one inline media part (image, video, or audio) for Gemini generateContent.
 */
export async function askGeminiGenerateContentWithInlineMedia(
  params: AskGeminiGenerateContentWithInlineMediaParams,
): Promise<AskLocalLlmConversationResult> {
  const { apiKey, model, userTextPrompt, inlineMimeType, inlineBase64, systemInstruction } = params;
  const key = apiKey.trim();
  if (!key) {
    return { ok: false, error: 'Missing API key' };
  }
  const modelId = model.replace(/^models\//, '').trim();
  if (!modelId) {
    return { ok: false, error: 'Missing model' };
  }
  const usr = userTextPrompt.trim();
  if (!usr) {
    return { ok: false, error: 'No prompt' };
  }
  if (!inlineBase64.trim()) {
    return { ok: false, error: 'No media data' };
  }
  const mime = inlineMimeType.trim() || 'application/octet-stream';

  const generationConfig = { maxOutputTokens: 8192, temperature: 0.3 };

  const inlinePart = { inlineData: { mimeType: mime, data: inlineBase64 } };

  const runGenerateContent = async (bodyStr: string): Promise<AskLocalLlmConversationResult> => {
    const versions = ['v1beta', 'v1'] as const;
    let lastError = '';
    let lastStatus: number | undefined;

    for (const ver of versions) {
      const url = `https://generativelanguage.googleapis.com/${ver}/models/${encodeURIComponent(
        modelId,
      )}:generateContent?key=${encodeURIComponent(key)}`;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: bodyStr,
      });

      if (res.ok) {
        const data = await res.json();
        const text = parseGeminiResponseJson(data);
        if (!text) {
          return { ok: false, error: 'Empty response from Gemini' };
        }
        return { ok: true, text };
      }

      lastStatus = res.status;
      lastError = await readHttpError(res);
      const retry =
        res.status === 404 ||
        lastError.includes('is not found') ||
        (lastError.includes('not found') && lastError.includes('API version')) ||
        lastError.includes('not supported for generateContent');
      if (!retry || ver === 'v1') {
        return { ok: false, error: lastError, status: lastStatus };
      }
    }

    return { ok: false, error: lastError || 'Gemini request failed', status: lastStatus };
  };

  const sys = systemInstruction?.trim();
  const tryDeveloperInstruction = geminiModelSupportsDeveloperInstruction(modelId) && Boolean(sys);

  const userPartsWithMedia = tryDeveloperInstruction && sys
    ? [{ text: usr }, inlinePart]
    : [{ text: sys ? mergeSystemAndUserForGemini(sys, usr) : usr }, inlinePart];

  const bodyWithSys = tryDeveloperInstruction && sys
    ? {
        systemInstruction: { parts: [{ text: sys }] },
        contents: [{ role: 'user', parts: userPartsWithMedia }],
        generationConfig,
      }
    : {
        contents: [{ role: 'user', parts: userPartsWithMedia }],
        generationConfig,
      };

  let first = await runGenerateContent(JSON.stringify(bodyWithSys));

  if (first.ok) return first;

  const err = first.error || '';
  const shouldRetryMerged =
    tryDeveloperInstruction &&
    (err.includes('Developer instruction') ||
      err.includes('system instruction') ||
      err.includes('systemInstruction') ||
      err.includes('not enabled for'));

  if (shouldRetryMerged) {
    const mergedParts = [{ text: mergeSystemAndUserForGemini(sys!, usr) }, inlinePart];
    first = await runGenerateContent(
      JSON.stringify({
        contents: [{ role: 'user', parts: mergedParts }],
        generationConfig,
      }),
    );
    if (first.ok) return first;
  }

  return first;
}

function openAiMessagesFromTurns(turns: LocalChatTurn[]) {
  return turns.map((t) => ({
    role: t.role === 'assistant' ? ('assistant' as const) : ('user' as const),
    content: t.content,
  }));
}

async function askOpenAIMultiTurn(
  apiKey: string,
  model: string,
  turns: LocalChatTurn[],
  systemPrompt?: string,
): Promise<AskLocalLlmConversationResult> {
  const sys = systemPrompt?.trim();
  const msgs = sys
    ? [{ role: 'system' as const, content: sys }, ...openAiMessagesFromTurns(turns)]
    : openAiMessagesFromTurns(turns);
  const res = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey.trim()}`,
    },
    body: JSON.stringify({
      model,
      messages: msgs,
      max_tokens: 4096,
      temperature: 0.4,
    }),
  });

  if (!res.ok) {
    return { ok: false, error: await readHttpError(res), status: res.status };
  }

  const data = (await res.json()) as {
    choices?: Array<{ message?: { content?: string } }>;
  };
  const text = data.choices?.[0]?.message?.content?.trim() ?? '';
  if (!text) {
    return { ok: false, error: 'Empty response from OpenAI' };
  }
  return { ok: true, text };
}

/** Single-turn OpenAI chat with a system prompt (e.g. Auto-Pilot, tools). */
export async function askOpenAIWithSystemInstruction(params: {
  apiKey: string;
  model: string;
  systemInstruction: string;
  userMessage: string;
}): Promise<AskLocalLlmConversationResult> {
  return askOpenAIMultiTurn(
    params.apiKey,
    params.model,
    [{ role: 'user', content: params.userMessage.trim() }],
    params.systemInstruction,
  );
}

export async function askLocalLlmConversation(params: AskLocalLlmConversationParams): Promise<AskLocalLlmConversationResult> {
  const { integrationId, apiKey, model, turns } = params;
  const key = apiKey.trim();
  if (!key) {
    return { ok: false, error: 'Missing API key' };
  }
  if (!model.trim()) {
    return { ok: false, error: 'Missing model' };
  }
  if (!turns.length) {
    return { ok: false, error: 'No messages' };
  }

  try {
    switch (integrationId) {
      case 'google_gemini_integration':
        return await askGeminiMultiTurn(key, model, turns, undefined);
      case 'openai_integration':
        return await askOpenAIMultiTurn(key, model, turns, undefined);
      default:
        return { ok: false, error: `Unsupported integration: ${integrationId}` };
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
      return {
        ok: false,
        error:
          'Network error — provider may block browser requests (CORS). Gemini usually works in-browser; OpenAI may require a server proxy.',
      };
    }
    return { ok: false, error: msg };
  }
}

export type AskLocalLlmWithSystemParams = {
  integrationId: string;
  apiKey: string;
  model: string;
  systemPrompt: string;
  turns: LocalChatTurn[];
};

/**
 * Multi-turn call with a separate system prompt (persona + rules). Used by Ask AI sidebar
 * with context injection in the latest user turn.
 */
export async function askLocalLlmConversationWithSystem(
  params: AskLocalLlmWithSystemParams,
): Promise<AskLocalLlmConversationResult> {
  const { integrationId, apiKey, model, systemPrompt, turns } = params;
  const key = apiKey.trim();
  if (!key) {
    return { ok: false, error: 'Missing API key' };
  }
  if (!model.trim()) {
    return { ok: false, error: 'Missing model' };
  }
  if (!turns.length) {
    return { ok: false, error: 'No messages' };
  }

  try {
    switch (integrationId) {
      case 'google_gemini_integration':
        return await askGeminiMultiTurn(key, model, turns, systemPrompt);
      case 'openai_integration':
        return await askOpenAIMultiTurn(key, model, turns, systemPrompt);
      default:
        return { ok: false, error: `Unsupported integration: ${integrationId}` };
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
      return {
        ok: false,
        error:
          'Network error — provider may block browser requests (CORS). Gemini usually works in-browser; OpenAI may require a server proxy.',
      };
    }
    return { ok: false, error: msg };
  }
}
